import { useCallback, useSyncExternalStore } from 'react';

/**
 * "Keep mail on this device for offline use" — the per-device switch over
 * everything ADR 0059 persists (ADR 0060).
 *
 * ## Why this is not a `UserPreferences` field
 *
 * The same reason the reading-pane position is not (`app/preferences.tsx`):
 * it describes **this screen**, not this person. A phone the owner never lends
 * out and a machine borrowed in a hotel lobby want opposite answers, and a
 * server-side preference would carry the hotel's answer home. It is also the
 * one setting that must keep working when the server cannot be reached, which
 * a `PUT /preferences` round trip cannot promise.
 *
 * So: `localStorage`, per browser, per device, and the settings page says so
 * in those words.
 *
 * ## The default is on, and an unreadable value is "on"
 *
 * On is what the app has done since #314, and offline reading is the reason
 * the cache exists. A corrupt or missing value therefore means *no answer has
 * been recorded*, not "off" — the app falls back to its shipped behaviour
 * rather than silently disabling a feature the user never turned off. Only the
 * exact string {@link OFF} is off, and only this module ever writes it.
 *
 * ## Reading it
 *
 * {@link keepMailOnDevice} for module code (the persister, the provider), and
 * {@link useKeepMailOnDevice} in React. The subscription carries `storage`
 * events too, so turning it off in one tab stops the persister in every tab of
 * this browser — which matters, because any one of them would otherwise write
 * the mailbox straight back to the disk the other just cleared.
 */

/** The `localStorage` key. Exported so the tests pin it rather than guess it. */
export const KEEP_MAIL_ON_DEVICE_KEY = 'ols-mail.keep-on-device';

const OFF = 'off';
const ON = 'on';

const listeners = new Set<() => void>();

function notify(): void {
  for (const listener of [...listeners]) listener();
}

/**
 * Whether this browser may keep a copy of the user's mail on disk.
 *
 * Never throws: storage can be blocked outright in a locked-down profile, and
 * a setting that cannot be read is a setting that was never written.
 */
export function keepMailOnDevice(): boolean {
  try {
    return localStorage.getItem(KEEP_MAIL_ON_DEVICE_KEY) !== OFF;
  } catch {
    return true;
  }
}

/**
 * Record the choice and tell everyone watching, in this order.
 *
 * The write comes first so that a listener which immediately calls
 * {@link keepMailOnDevice} — the provider does — sees the new answer rather
 * than the one it is reacting to.
 */
export function setKeepMailOnDevice(next: boolean): void {
  try {
    localStorage.setItem(KEEP_MAIL_ON_DEVICE_KEY, next ? ON : OFF);
  } catch {
    // Storage blocked. The choice holds for this page's lifetime — listeners
    // are still told — it just will not survive a reload, and nothing here
    // may pretend otherwise.
  }
  notify();
}

/** Subscribe to changes made here *or* in another tab of this browser. */
export function subscribeKeepMailOnDevice(listener: () => void): () => void {
  listeners.add(listener);
  const onStorage = (event: StorageEvent): void => {
    // `key === null` is a `localStorage.clear()` from another tab.
    if (event.key !== null && event.key !== KEEP_MAIL_ON_DEVICE_KEY) return;
    listener();
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(listener);
    window.removeEventListener('storage', onStorage);
  };
}

/** The setting and its setter, for the settings page and the offline notices. */
export function useKeepMailOnDevice(): [boolean, (next: boolean) => void] {
  // `useSyncExternalStore`: the snapshot is a boolean, so it is stable by
  // value, and the store really is external — `localStorage` plus a `storage`
  // event from a tab this React tree knows nothing about.
  const keep = useSyncExternalStore(
    subscribeKeepMailOnDevice,
    keepMailOnDevice,
    // Server-rendered/prerendered: assume the shipped default rather than
    // touching storage that is not there.
    () => true,
  );
  const set = useCallback((next: boolean) => setKeepMailOnDevice(next), []);
  return [keep, set];
}
